import React, { useEffect, useRef, useState, useCallback, useImperativeHandle, forwardRef, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, IconButton, Popper, Input, Paper, Typography } from '@mui/material'
import { Icon as Iconify } from '@iconify/react'
import ClearIcon from '@mui/icons-material/Clear'
import SearchIcon from '@mui/icons-material/Search'
import classNames from 'classnames'
import useInnovaTheme from './hooks/useInnovaTheme'
import { getItemFromLS, addItemToLS } from 'utils/localStorage'
import { fuzzyMatch } from 'utils/searchFunctions'

const LOCAL_STORAGE_KEY = 'recentSearches'
const DEFAULT_ICON = 'mdi:recent'

const getTermText = (item) => (typeof item === 'string' ? item : item.term)

const getIconName = (item) => {
  if (!item || !item.icon) return DEFAULT_ICON

  return item.icon
}

export const injectTermToSearchHistory = (recentKey, item) => {
  if (!recentKey || !item) return
  let existingHistory = []
  const unparsed = getItemFromLS(LOCAL_STORAGE_KEY, recentKey)
  if (unparsed) {
    try {
      existingHistory = JSON.parse(unparsed)
    } catch (e) {
      console.error('Failed to parse recent search history:', e)
    }
  }

  if (!Array.isArray(existingHistory)) return

  const updatedHistory = [item, ...existingHistory.filter((t) => getTermText(t) !== getTermText(item))].slice(0, 10)
  addItemToLS(LOCAL_STORAGE_KEY, recentKey, JSON.stringify(updatedHistory))
  return updatedHistory
}

const SearchListItem = ({ term, icon, onClick, onDelete }) => {
  const { getTextColor } = useInnovaTheme()
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '4px 8px',
        cursor: 'pointer',
        borderRadius: 3,
        transition: 'background-color 0.2s ease',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.1)',
        },
        color: getTextColor(),
      }}
      onClick={onClick}
      onMouseDown={(e) => e.preventDefault()}>
      <Iconify icon={icon} width={18} height={18} color={getTextColor()} />
      <Typography variant='body' sx={{ flex: 1, ml: 2, mb: 0.5 }}>
        {term}
      </Typography>
      <IconButton
        onClick={(e) => {
          e.stopPropagation()
          onDelete()
        }}
        size='small'
        sx={{
          color: getTextColor(),
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          },
        }}>
        <ClearIcon fontSize='small' />
      </IconButton>
    </Box>
  )
}

const SearchBarWithRecent = forwardRef(
  (
    {
      cancelOnEscape,
      className,
      closeIcon,
      disabled,
      onCancelSearch,
      onKeyDown,
      searchIcon,
      style,
      recentKey,
      overlayMaxWidth,
      ...inputProps
    },
    ref,
  ) => {
    const inputRef = useRef()
    const autoFocusRef = useRef(inputProps?.autoFocus || false)
    const [value, setValue] = useState(inputProps.value || '')
    const [history, setHistory] = useState([])
    const [focused, setFocused] = useState(false)
    const { getTextColor, theme: innovaTheme } = useInnovaTheme()
    const containerRef = useRef(null)

    useEffect(() => {
      if (recentKey) {
        const storedHistory = getItemFromLS(LOCAL_STORAGE_KEY, recentKey)
        if (storedHistory) {
          setHistory(JSON.parse(storedHistory))
        }
      }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      setValue(inputProps.value || '')

      if (autoFocusRef.current && inputProps.value && inputRef.current) {
        handleFocus()
        autoFocusRef.current = false
      }
    }, [inputProps.value]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleHistoryChange = useCallback(
      (newHistory) => {
        if (!recentKey) return

        setHistory(newHistory)
        addItemToLS(LOCAL_STORAGE_KEY, recentKey, JSON.stringify(newHistory))
      },
      [recentKey],
    )

    const handleFocus = useCallback(
      (e) => {
        if (focused) return

        // if autofocus is enabled, do not display history immediately
        if (e && e.type === 'focus' && e.target !== inputRef.current) return

        setFocused(true)
        inputProps.onFocus?.(e)
      },
      [inputProps], // eslint-disable-line react-hooks/exhaustive-deps
    )

    const handleBlur = useCallback(
      (e) => {
        setFocused(false)
        setValue((v) => v.trim())
        inputProps.onBlur?.(e)
      },
      [inputProps],
    )

    const handleInput = useCallback(
      (e) => {
        setValue(e.target.value)
        inputProps.onChange?.(e.target.value, false)
      },
      [inputProps],
    )

    const handleCancel = useCallback(() => {
      setValue('')
      onCancelSearch?.()
    }, [onCancelSearch])

    const handleKeyUp = useCallback(
      (e) => {
        if (e.key === 'Enter') {
          const term = value.trim()
          if (term) {
            handleHistoryChange([term, ...history.filter((t) => t !== term)].slice(0, 10))
            inputProps.onChange?.(term)
          }
          setFocused(false)
        } else if (e.key === 'Escape') {
          if (cancelOnEscape) {
            handleCancel()
          }
          inputRef.current?.blur()
        }
        inputProps.onKeyUp?.(e)
      },
      [cancelOnEscape, handleCancel, history, handleHistoryChange, inputProps, value],
    )

    const handleChipClick = useCallback(
      (term, icon) => {
        setValue(term)
        const isWell = icon === 'fa6-solid:oil-well'
        inputProps.onChange?.(term, isWell)
        if (isWell) {
          return
        }

        setFocused(false)

        // preserve object structure if unique icon given
        let existingTerm = history.find((h) => getTermText(h) === term)
        if (existingTerm && typeof existingTerm === 'object') {
          term = existingTerm
        }

        handleHistoryChange([term, ...history.filter((t) => t !== term)].slice(0, 10))
      },
      [history, handleHistoryChange, inputProps],
    )

    useImperativeHandle(
      ref,
      () => ({
        focus: () => inputRef.current?.focus(),
        blur: () => inputRef.current?.blur(),
      }),
      [],
    )

    const isSearchHidden = !!value.trim()
    const isClearHidden = !value.trim()

    const filteredHistory = useMemo(() => {
      if (history.length === 0) return []

      const v = value.trim().toLowerCase()
      if (!v) return history

      return history.filter((item) => {
        const term = getTermText(item)
        return fuzzyMatch(v, term)
      })
    }, [value, history])

    const searchBarHeight = style.size === 'small' ? 32 : style.size === 'large' ? 40 : 36

    return (
      <Paper
        ref={containerRef}
        className={classNames(className)}
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          p: 0.5,
          overflow: 'visible',
          width: '100%',
          borderBottomLeftRadius: focused && history.length > 0 ? 0 : 5,
          borderBottomRightRadius: focused && history.length > 0 ? 0 : 5,
          backgroundColor: focused ? style.focusedColor : style.backgroundColor,
          border: style.border,
          borderBottom: focused && history.length > 0 ? 'none' : style.border,
          flex: style.flex,
          transition: 'background-color 0.1s ease-in-out, border 0.1s ease-in-out',
          '&:hover': {
            backgroundColor: style.focusedColor,
          },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', height: searchBarHeight }}>
          <Input
            {...inputProps}
            inputRef={inputRef}
            value={value}
            onChange={handleInput}
            onBlur={handleBlur}
            onClick={focused ? () => inputRef.current.blur() : handleFocus}
            onKeyDown={onKeyDown}
            onKeyUp={handleKeyUp}
            fullWidth
            disableUnderline
            disabled={disabled}
            sx={{
              flex: 1,
              overflow: 'visible',
              '& .MuiInputBase-root': {
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                overflow: 'visible',
              },
              '& .MuiInputBase-input': {
                color: `${getTextColor()} !important`,
                height: '100%',
                boxSizing: 'border-box',
                overflow: 'visible',
                whiteSpace: 'normal',
              },
            }}
          />
          <IconButton
            onClick={() => {
              const term = value.trim()
              if (term) {
                handleHistoryChange([term, ...history.filter((t) => t !== term)].slice(0, 10))
                inputProps.onChange?.(term, false)
              }
            }}
            disabled={disabled || isSearchHidden}
            sx={(theme) => ({
              transform: isSearchHidden ? 'scale(0)' : 'scale(1)',
              transition: theme.transitions.create(['transform', 'color'], {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut,
              }),
              alignSelf: 'center',
              mr: -6,
            })}
            size='large'>
            {React.cloneElement(searchIcon, {
              sx: (theme) =>
                theme.transitions.create('opacity', {
                  duration: theme.transitions.duration.shorter,
                  easing: theme.transitions.easing.easeInOut,
                }),
            })}
          </IconButton>
          <IconButton
            onClick={handleCancel}
            disabled={disabled || isClearHidden}
            sx={(theme) => ({
              mr: 0.5,
              color: getTextColor(),
              transform: isClearHidden ? 'scale(0)' : 'scale(1)',
              transition: theme.transitions.create(['transform', 'color'], {
                duration: theme.transitions.duration.shorter,
                easing: theme.transitions.easing.easeInOut,
              }),
              alignSelf: 'center',
            })}
            size='large'>
            {React.cloneElement(closeIcon, {
              sx: (theme) =>
                theme.transitions.create('opacity', {
                  duration: theme.transitions.duration.shorter,
                  easing: theme.transitions.easing.easeInOut,
                }),
            })}
          </IconButton>
        </Box>
        {focused && filteredHistory.length > 0 && (
          <Popper
            open={focused && filteredHistory.length > 0}
            anchorEl={containerRef.current}
            placement='bottom-end'
            style={{ zIndex: 1300, width: containerRef.current?.offsetWidth }}
            modifiers={[{ name: 'offset', options: { offset: [0, 0] } }]}>
            <Box
              sx={() => {
                const isDark = innovaTheme === 'dark'
                return {
                  maxHeight: 300,
                  overflowY: 'auto',
                  bgcolor: style.focusedColor,
                  borderTop: 'none',
                  borderBottomLeftRadius: 5,
                  borderBottomRightRadius: 5,
                  boxSizing: 'border-box',
                  outline: isDark ? style.border : 'none',
                  outlineOffset: isDark ? '.5px' : '0',
                  colorScheme: innovaTheme === 'dark' ? 'dark' : 'light',
                  scrollbarWidth: 'thin',
                }
              }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, p: 1 }}>
                {filteredHistory.map((item) => {
                  const term = getTermText(item)
                  const icon = getIconName(item)
                  return (
                    <SearchListItem
                      key={term}
                      term={term}
                      icon={icon}
                      onClick={() => handleChipClick(term, icon)}
                      onDelete={() => handleHistoryChange(history.filter((h) => getTermText(h) !== term))}
                    />
                  )
                })}
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: '2px' }}>
                <Button
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => handleHistoryChange([])}
                  sx={(theme) => ({
                    color: theme.palette.error.dark,
                    textTransform: 'none',
                    '&:hover': { backgroundColor: theme.palette.action.hover },
                  })}>
                  Clear
                </Button>
              </Box>
            </Box>
          </Popper>
        )}
      </Paper>
    )
  },
)

SearchBarWithRecent.defaultProps = {
  cancelOnEscape: false,
  className: '',
  closeIcon: <ClearIcon />,
  disabled: false,
  placeholder: 'Search',
  searchIcon: <SearchIcon />,
  style: undefined,
  value: '',
  recentKey: '',
  overlayMaxWidth: null,
}

SearchBarWithRecent.propTypes = {
  cancelOnEscape: PropTypes.bool,
  className: PropTypes.string,
  closeIcon: PropTypes.node,
  disabled: PropTypes.bool,
  onCancelSearch: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  searchIcon: PropTypes.node,
  style: PropTypes.object,
  value: PropTypes.string,
  recentKey: PropTypes.string,
  overlayMaxWidth: PropTypes.string,
}

export default SearchBarWithRecent
